import type { ReactNode } from 'react';
import { Badge, Button, Card, CardHeader, CardTitle } from '@paige/ui';
import type { EnterpriseQuote, PartnerBillingView } from './pb-types';
import { channelWord, day, dayBefore, intervalWord, planWord, quoteTerms } from './pb-words';
import { QuoteList } from './QuoteList';
import { Banner, FactList } from './pb-parts';
import './partner-billing.css';

/** The writes the panel offers; each opens its dialog. */
export type PanelAction =
  | { kind: 'take-on' | 'suspend' | 'resume' | 'quote' | 'card-move' | 'call-off' | 'end' }
  | { kind: 'withdraw'; quote: EnterpriseQuote };

export interface PartnerBillingPanelProps {
  /** Undefined while it is read; null when the company pays Ademero itself and no card move was asked. */
  billing: PartnerBillingView | null | undefined;
  quotes: EnterpriseQuote[] | undefined;
  /** The Ademero invoice's switch is off (the API's 503 `invoice_channel_not_enabled`). */
  channelOff: boolean;
  failed: boolean;
  /** The company pays Ademero by card now: the one it can move into partner billing. */
  paysByCard: boolean;
  /** Billing or engineering staff: the writes are theirs. */
  canWrite: boolean;
  renderAccountLink: (zohoAccountId: string, label: string) => ReactNode;
  onAction: (action: PanelAction) => void;
}

const managed = (b: PartnerBillingView | null | undefined): b is PartnerBillingView =>
  Boolean(b?.billingAccount);

/**
 * The Customer page's Partner billing panel (ADR-0036, section 7.1): who pays, on which invoice, the plan or
 * quote, the period, what waits for the renewal, a pause or lapse, and a move from a card plan; with the
 * writes for billing staff. Everyone in the admin area reads it.
 */
export function PartnerBillingPanel(props: PartnerBillingPanelProps): ReactNode {
  const b = props.billing;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Partner billing</CardTitle>
        {managed(b) && b.invoiceChannel ? (
          <Badge tone={b.invoiceChannel === 'quickbooks' ? 'accent' : 'neutral'}>
            {channelWord[b.invoiceChannel]}
          </Badge>
        ) : null}
      </CardHeader>
      <PanelBody {...props} />
    </Card>
  );
}

function PanelBody(props: PartnerBillingPanelProps): ReactNode {
  const b = props.billing;
  if (props.failed) return <p className="ad-small ad-danger">Partner billing could not be read.</p>;
  if (b === undefined) return <p className="ad-small pui-muted">Reading partner billing…</p>;
  return (
    <>
      {managed(b) ? (
        <Facts b={b} link={props.renderAccountLink} />
      ) : (
        <p className="ad-small">
          {props.paysByCard ? 'Pays Ademero itself, by card.' : 'Pays Ademero itself.'}
        </p>
      )}
      {managed(b) ? <States b={b} /> : null}
      {b?.cardMove ? <CardMoveBanner b={b} link={props.renderAccountLink} /> : null}
      {props.channelOff ? (
        <p className="pb-off" role="status">
          The QuickBooks channel (Ademero invoice) is not switched on yet, so pauses, quotes and
          card moves wait for it.
        </p>
      ) : (
        <QuoteList
          quotes={props.quotes}
          canWrite={props.canWrite}
          onWithdraw={(quote) => props.onAction({ kind: 'withdraw', quote })}
        />
      )}
      {props.canWrite ? <Actions {...props} /> : null}
    </>
  );
}

/** The period on the statement channel (Paige keeps it), or the renewal online. */
const periodFact = (b: PartnerBillingView) =>
  b.periodStart
    ? {
        label: 'Period',
        value: `${day(b.periodStart)} to ${day(dayBefore(b.renewsOn))}`,
        note: `renews ${day(b.renewsOn)}`,
      }
    : { label: 'Renews', value: day(b.renewsOn) };

function Facts(p: { b: PartnerBillingView; link: PartnerBillingPanelProps['renderAccountLink'] }) {
  const b = p.b;
  const plan = b.quote
    ? `Enterprise, ${quoteTerms(b.quote)}`
    : `${planWord(b.package)}, ${intervalWord(b.interval)}`;
  const payer = b.billingAccount ?? '';
  const facts = [
    {
      label: 'Paid by',
      value: p.link(payer, b.partner ?? payer),
      note: `${b.partnerCode ?? ''} · since ${day(b.since)}`,
    },
    { label: 'Plan', value: plan, note: b.quote?.quoteRef ? `Quote ${b.quote.quoteRef}` : null },
    periodFact(b),
  ];
  return <FactList facts={facts} />;
}

/** What changes what the company gets, or what waits for the renewal. */
function States({ b }: { b: PartnerBillingView }): ReactNode {
  return (
    <>
      {b.suspended ? (
        <Banner tone="warning" title={`Paused ${day(b.suspended.at)} by ${b.suspended.by}`}>
          {b.suspended.why ?? 'No reason given.'} The company is on Free; billing carries on.
        </Banner>
      ) : null}
      {b.lapsedAt ? (
        <Banner tone="danger" title={`Lapsed ${day(b.lapsedAt)}`}>
          The paying account is suspended or its invoice unpaid: the company is on Free.
        </Banner>
      ) : null}
      {b.endsOn ? (
        <Banner title={`Stops ${day(b.endsOn)}`}>
          Partner billing ends then; the company becomes a direct customer on Free.
        </Banner>
      ) : null}
      {b.changesTo ? (
        <Banner title={`Changes ${day(b.changesTo.on)}`}>
          To {planWord(b.changesTo.package)}, {intervalWord(b.changesTo.interval)}, at its renewal.
        </Banner>
      ) : null}
      {b.channelMovesOn && b.invoiceChannel ? (
        <Banner title={`Moves ${day(b.channelMovesOn)}`}>
          From the {channelWord[b.invoiceChannel].toLowerCase()} to the{' '}
          {channelWord[b.invoiceChannel === 'quickbooks' ? 'stripe' : 'quickbooks'].toLowerCase()}{' '}
          at its renewal.
        </Banner>
      ) : null}
    </>
  );
}

function CardMoveBanner({
  b,
  link,
}: {
  b: PartnerBillingView;
  link: PartnerBillingPanelProps['renderAccountLink'];
}) {
  const m = b.cardMove;
  if (!m) return null;
  const plan = `${planWord(m.package)}, ${intervalWord(m.interval)}`;
  if (m.state === 'scheduled')
    return (
      <Banner title={`Card plan moves to partner billing ${day(m.takesOverOn)}`}>
        {link(m.zohoAccountId, 'Its partner')} pays from then: {plan}. Asked by {m.requestedBy}:{' '}
        {m.reason}
      </Banner>
    );
  if (m.state === 'needs_staff')
    return (
      <Banner title="Card move needs a person">
        Part done at the payment provider and no longer takeable
        {m.settledWhy ? `: ${m.settledWhy}` : ''}. Take the company on, or bill it, by hand.
      </Banner>
    );
  const word = m.state === 'done' ? 'Moved from its card plan' : 'Card move called off';
  return (
    <p className="ad-small pui-muted">
      {word} {day(m.settledAt)}
      {m.settledWhy ? `: ${m.settledWhy}` : ''}.
    </p>
  );
}

type Kind = Exclude<PanelAction, { kind: 'withdraw' }>['kind'];

/** A company no partner pays for: take it on, move its card plan, or record a quote; a scheduled card move can be called off. */
function DirectActions(p: PartnerBillingPanelProps & { act: (k: Kind) => () => void }): ReactNode {
  const off = p.channelOff;
  const scheduled = p.billing?.cardMove?.state === 'scheduled';
  return (
    <div className="pb-actions">
      {scheduled ? (
        <Button onClick={p.act('call-off')} disabled={off}>
          Call off card move…
        </Button>
      ) : (
        <Button onClick={p.act('take-on')}>Partner pays for it…</Button>
      )}
      {p.paysByCard && !scheduled ? (
        <Button onClick={p.act('card-move')} disabled={off}>
          Move card plan to partner…
        </Button>
      ) : null}
      <Button variant="ghost" onClick={p.act('quote')} disabled={off}>
        Record Enterprise quote…
      </Button>
    </div>
  );
}

/** The writes that fit the company's state; the API refuses the rest in its own words. */
function Actions(props: PartnerBillingPanelProps): ReactNode {
  const b = props.billing;
  const act = (kind: Kind) => () => props.onAction({ kind });
  const off = props.channelOff;
  if (!managed(b)) return <DirectActions {...props} act={act} />;
  return (
    <div className="pb-actions">
      {b.suspended ? (
        <Button onClick={act('resume')} disabled={off}>
          Resume company…
        </Button>
      ) : (
        <Button onClick={act('suspend')} disabled={off}>
          Pause company…
        </Button>
      )}
      <Button onClick={act('quote')} disabled={off || b.invoiceChannel !== 'quickbooks'}>
        {b.quote ? 'Replace Enterprise quote…' : 'Record Enterprise quote…'}
      </Button>
      <Button variant="danger" onClick={act('end')}>
        End partner billing now…
      </Button>
    </div>
  );
}
